/**
 * `[data-tour-id]` anchor lookup shared by the tour's indicator layer and its driver (CNCT-6771).
 *
 * design-contract.md → §Surface 3.1 (`data-tour-id` anchor convention) / §Surface 3.2 (indicator
 * placement is anchored).
 *
 * An anchor slug is NOT unique in the DOM: the left-nav menu renders once in the desktop rail and once
 * more inside the (always-mounted, usually hidden) mobile drawer, so `nav-messages` and friends match
 * twice. `document.querySelector` would hand back whichever comes first in source order — the desktop
 * copy, which is `display:none` below the `lg` breakpoint. Prefer the copy that actually has a
 * laid-out box so the ring lands on the element the user can see.
 */

/** True when `el` has a non-degenerate layout box (i.e. it is rendered, not `display:none`). */
export function hasLayoutBox(el: Element): boolean {
  const rect = el.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

/**
 * The element for `anchor`, preferring a laid-out copy over a hidden duplicate. Falls back to the
 * first match when no copy has a box (the anchor exists but nothing is rendered — jsdom, or a rail
 * hidden at this breakpoint), and `null` when the anchor is absent entirely. Callers that draw on the
 * rect must still reject a degenerate rect; callers that only ask "does this target exist for this
 * user?" (the graceful-skip check) want the existence answer this returns.
 */
export function findTourAnchor(doc: Document, anchor: string): HTMLElement | null {
  const matches = Array.from(doc.querySelectorAll<HTMLElement>(`[data-tour-id="${anchor}"]`))
  return matches.find(hasLayoutBox) ?? matches[0] ?? null
}

/** Max polls before an anchor is treated as absent — bounded, so a resolve never hangs. */
export const MAX_ANCHOR_ATTEMPTS = 20

/**
 * Poll for `anchor` a bounded number of times so a not-yet-rendered element (a nav group still
 * expanding, a dropdown mid-transition) gets a brief chance to appear, then resolve `null` so the
 * caller can skip the step gracefully. Mirrors `TourEngineService.resolveAnchor`'s bounded-poll shape.
 */
export function waitForTourAnchor(doc: Document, anchor: string, maxAttempts = MAX_ANCHOR_ATTEMPTS): Promise<HTMLElement | null> {
  return new Promise<HTMLElement | null>(resolve => {
    let attempts = 0
    const tick = () => {
      const el = findTourAnchor(doc, anchor)
      if (el) {
        resolve(el)
        return
      }
      if (++attempts >= maxAttempts) {
        resolve(null)
        return
      }
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(tick)
      } else {
        setTimeout(tick, 16)
      }
    }
    tick()
  })
}
